import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useActiveAccount } from "thirdweb/react";
import { ThirdwebWalletConnect } from "../components/ThirdwebWalletConnect";
import { inMetamaskBrowser, openInMetamask } from "../utils/metamask";
import { useWalletKind } from "../hooks/useWalletKind";

type DrawerType = "wallet_connect" | "open_in_metamask" | "scan_chip" | null;

export default function Home() {
  const navigate = useNavigate();
  const account = useActiveAccount();
  const address = account?.address;
  const walletKind = useWalletKind();

  const [currentDrawer, setCurrentDrawer] = useState<DrawerType>(null);

  useEffect(() => {
    if (address && currentDrawer === "wallet_connect") {
      if (walletKind === "metamask" && !inMetamaskBrowser()) {
        setCurrentDrawer("open_in_metamask");
      } else {
        setCurrentDrawer("scan_chip");
      }
    }
  }, [address, currentDrawer, walletKind]);

  useEffect(() => {
    if (currentDrawer) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [currentDrawer]);

  return (
    <>
      <div className="p-6 space-y-5">
        <div className="py-[22px] rounded-[12px] bg-gradient-to-r from-[#57E9F9] to-[#3BDD77] flex flex-col items-center justify-center gap-2">
          <div className="font-bold text-center text-black">
            Sign up for 223 and Claim <br /> Digital Collectibles in a Flash
          </div>

          <button
            onClick={() => setCurrentDrawer("wallet_connect")}
            className="bg-[#343434] h-10 text-white font-bold rounded-[50px] px-10 hover:bg-[#4a4a4a] transition-colors cursor-pointer"
          >
            Claim Now
          </button>
        </div>

        <div className="bg-gradient-to-br from-[#FED7AA] to-[#FB7185] rounded-[14px] p-6 w-full relative overflow-hidden">
          <img
            src="/images/stars-bg.png"
            alt="starts-bg"
            className="object-cover absolute inset-0 h-full w-full opacity-30"
          />

          <img
            src="/images/chimpers/chimpers-logo-xl.png"
            alt="chimpers-logo"
            className="relative z-10 mt-[-13px] mx-auto"
          />

          <button
            onClick={() => navigate("/claim/chimpers")}
            className="bg-gradient-to-br from-[#FED7AA] to-[#FB7185] text-white text-lg font-bold rounded-lg px-2 h-12 flex items-center justify-center gap-1.5 w-full relative z-10 shadow-lg hover:opacity-90 transition-opacity cursor-pointer"
          >
            <span>View Chimpers</span>

            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.47 8.53003C13.3375 8.38785 13.2654 8.19981 13.2688 8.00551C13.2723 7.81121 13.351 7.62582 13.4884 7.48841C13.6258 7.35099 13.8112 7.27228 14.0055 7.26885C14.1998 7.26543 14.3878 7.33755 14.53 7.47003L18.53 11.47C18.6705 11.6107 18.7493 11.8013 18.7493 12C18.7493 12.1988 18.6705 12.3894 18.53 12.53L14.53 16.53C14.4613 16.6037 14.3785 16.6628 14.2865 16.7038C14.1945 16.7448 14.0952 16.7668 13.9945 16.7686C13.8938 16.7704 13.7938 16.7519 13.7004 16.7142C13.607 16.6764 13.5222 16.6203 13.451 16.5491C13.3797 16.4778 13.3236 16.393 13.2859 16.2996C13.2482 16.2062 13.2296 16.1062 13.2314 16.0055C13.2332 15.9048 13.2552 15.8055 13.2962 15.7135C13.3372 15.6215 13.3963 15.5387 13.47 15.47L16.19 12.75H6.5C6.30109 12.75 6.11032 12.671 5.96967 12.5304C5.82902 12.3897 5.75 12.1989 5.75 12C5.75 11.8011 5.82902 11.6104 5.96967 11.4697C6.11032 11.329 6.30109 11.25 6.5 11.25H16.19L13.47 8.53003Z"
                fill="white"
              />
            </svg>
          </button>
        </div>

        <div className="bg-gradient-to-br from-[#C4B5FD] to-[#6366F1] rounded-[14px] p-6 w-full relative overflow-hidden flex flex-col justify-end gap-3">
          <img
            src="/images/stars-bg.png"
            alt="starts-bg"
            className="object-cover absolute inset-0 h-full w-full opacity-30"
          />

          <img
            src="/images/ponke/ponke-logo-xl.png"
            alt="ponke-logo"
            className="relative z-10 mx-auto"
          />

          <button
            onClick={() => navigate("/claim/ponke")}
            className="bg-gradient-to-br from-[#C4B5FD] to-[#6366F1] text-white text-lg font-bold rounded-lg px-2 h-12 flex items-center justify-center gap-1.5 w-full relative z-10 shadow-lg hover:opacity-90 transition-opacity cursor-pointer"
          >
            <span>View Ponke</span>

            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.47 8.53003C13.3375 8.38785 13.2654 8.19981 13.2688 8.00551C13.2723 7.81121 13.351 7.62582 13.4884 7.48841C13.6258 7.35099 13.8112 7.27228 14.0055 7.26885C14.1998 7.26543 14.3878 7.33755 14.53 7.47003L18.53 11.47C18.6705 11.6107 18.7493 11.8013 18.7493 12C18.7493 12.1988 18.6705 12.3894 18.53 12.53L14.53 16.53C14.4613 16.6037 14.3785 16.6628 14.2865 16.7038C14.1945 16.7448 14.0952 16.7668 13.9945 16.7686C13.8938 16.7704 13.7938 16.7519 13.7004 16.7142C13.607 16.6764 13.5222 16.6203 13.451 16.5491C13.3797 16.4778 13.3236 16.393 13.2859 16.2996C13.2482 16.2062 13.2296 16.1062 13.2314 16.0055C13.2332 15.9048 13.2552 15.8055 13.2962 15.7135C13.3372 15.6215 13.3963 15.5387 13.47 15.47L16.19 12.75H6.5C6.30109 12.75 6.11032 12.671 5.96967 12.5304C5.82902 12.3897 5.75 12.1989 5.75 12C5.75 11.8011 5.82902 11.6104 5.96967 11.4697C6.11032 11.329 6.30109 11.25 6.5 11.25H16.19L13.47 8.53003Z"
                fill="white"
              />
            </svg>
          </button>
        </div>

        <div className="bg-gradient-to-br from-[#FFCE7A] to-[#F17941] rounded-[14px] p-6 w-full relative overflow-hidden flex flex-col justify-end gap-3">
          <img
            src="/images/stars-bg.png"
            alt="starts-bg"
            className="object-cover absolute inset-0 h-full w-full opacity-30"
          />

          <img
            src="/images/catgame/catgame-logo.png"
            alt="catgame-logo"
            className="relative z-10 mx-auto max-w-[110px]"
          />

          <button
            onClick={() => navigate("/claim/catgame")}
            className="bg-gradient-to-br from-[#F17941] to-[#FFCE7A] text-white text-lg font-bold rounded-lg px-2 h-12 flex items-center justify-center gap-1.5 w-full relative z-10 shadow-lg hover:opacity-90 transition-opacity cursor-pointer"
          >
            <span>View Catgame</span>

            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.47 8.53003C13.3375 8.38785 13.2654 8.19981 13.2688 8.00551C13.2723 7.81121 13.351 7.62582 13.4884 7.48841C13.6258 7.35099 13.8112 7.27228 14.0055 7.26885C14.1998 7.26543 14.3878 7.33755 14.53 7.47003L18.53 11.47C18.6705 11.6107 18.7493 11.8013 18.7493 12C18.7493 12.1988 18.6705 12.3894 18.53 12.53L14.53 16.53C14.4613 16.6037 14.3785 16.6628 14.2865 16.7038C14.1945 16.7448 14.0952 16.7668 13.9945 16.7686C13.8938 16.7704 13.7938 16.7519 13.7004 16.7142C13.607 16.6764 13.5222 16.6203 13.451 16.5491C13.3797 16.4778 13.3236 16.393 13.2859 16.2996C13.2482 16.2062 13.2296 16.1062 13.2314 16.0055C13.2332 15.9048 13.2552 15.8055 13.2962 15.7135C13.3372 15.6215 13.3963 15.5387 13.47 15.47L16.19 12.75H6.5C6.30109 12.75 6.11032 12.671 5.96967 12.5304C5.82902 12.3897 5.75 12.1989 5.75 12C5.75 11.8011 5.82902 11.6104 5.96967 11.4697C6.11032 11.329 6.30109 11.25 6.5 11.25H16.19L13.47 8.53003Z"
                fill="white"
              />
            </svg>
          </button>
        </div>

        <div className="bg-gradient-to-br from-[#FD90E9] to-[#852A93] rounded-[14px] p-6 w-full relative overflow-hidden flex flex-col justify-end gap-3">
          <img
            src="/images/stars-bg.png"
            alt="starts-bg"
            className="object-cover absolute inset-0 h-full w-full opacity-30"
          />

          <img
            src="/images/mew/mew-logo.png"
            alt="catgame-logo"
            className="relative z-10 mx-auto max-w-[148px]"
          />

          <button
            onClick={() => navigate("/claim/mew")}
            className="bg-gradient-to-br from-[#852A93] to-[#FD90E9] text-white text-lg font-bold rounded-lg px-2 h-12 flex items-center justify-center gap-1.5 w-full relative z-10 shadow-lg hover:opacity-90 transition-opacity cursor-pointer"
          >
            <span>View Mew</span>

            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.47 8.53003C13.3375 8.38785 13.2654 8.19981 13.2688 8.00551C13.2723 7.81121 13.351 7.62582 13.4884 7.48841C13.6258 7.35099 13.8112 7.27228 14.0055 7.26885C14.1998 7.26543 14.3878 7.33755 14.53 7.47003L18.53 11.47C18.6705 11.6107 18.7493 11.8013 18.7493 12C18.7493 12.1988 18.6705 12.3894 18.53 12.53L14.53 16.53C14.4613 16.6037 14.3785 16.6628 14.2865 16.7038C14.1945 16.7448 14.0952 16.7668 13.9945 16.7686C13.8938 16.7704 13.7938 16.7519 13.7004 16.7142C13.607 16.6764 13.5222 16.6203 13.451 16.5491C13.3797 16.4778 13.3236 16.393 13.2859 16.2996C13.2482 16.2062 13.2296 16.1062 13.2314 16.0055C13.2332 15.9048 13.2552 15.8055 13.2962 15.7135C13.3372 15.6215 13.3963 15.5387 13.47 15.47L16.19 12.75H6.5C6.30109 12.75 6.11032 12.671 5.96967 12.5304C5.82902 12.3897 5.75 12.1989 5.75 12C5.75 11.8011 5.82902 11.6104 5.96967 11.4697C6.11032 11.329 6.30109 11.25 6.5 11.25H16.19L13.47 8.53003Z"
                fill="white"
              />
            </svg>
          </button>
        </div>

        <div className="bg-gradient-to-br from-[#FFE082] to-[#FF9F1C] rounded-[14px] p-6 w-full relative overflow-hidden flex flex-col justify-end gap-3">
          <div className="relative z-10 mx-auto rounded-full bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#111111]">
            Nobody Sausage
          </div>

          <div className="relative z-10 text-center text-2xl font-bold text-[#111111]">
            MetaMask + Glyph
          </div>

          <button
            onClick={() => navigate("/claim/nobodysausage")}
            className="bg-gradient-to-br from-[#F8C655] to-[#F09A12] text-white text-lg font-bold rounded-lg px-2 h-12 flex items-center justify-center gap-1.5 w-full relative z-10 shadow-lg hover:opacity-90 transition-opacity cursor-pointer"
          >
            <span>View Nobody Sausage</span>

            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.47 8.53003C13.3375 8.38785 13.2654 8.19981 13.2688 8.00551C13.2723 7.81121 13.351 7.62582 13.4884 7.48841C13.6258 7.35099 13.8112 7.27228 14.0055 7.26885C14.1998 7.26543 14.3878 7.33755 14.53 7.47003L18.53 11.47C18.6705 11.6107 18.7493 11.8013 18.7493 12C18.7493 12.1988 18.6705 12.3894 18.53 12.53L14.53 16.53C14.4613 16.6037 14.3785 16.6628 14.2865 16.7038C14.1945 16.7448 14.0952 16.7668 13.9945 16.7686C13.8938 16.7704 13.7938 16.7519 13.7004 16.7142C13.607 16.6764 13.5222 16.6203 13.451 16.5491C13.3797 16.4778 13.3236 16.393 13.2859 16.2996C13.2482 16.2062 13.2296 16.1062 13.2314 16.0055C13.2332 15.9048 13.2552 15.8055 13.2962 15.7135C13.3372 15.6215 13.3963 15.5387 13.47 15.47L16.19 12.75H6.5C6.30109 12.75 6.11032 12.671 5.96967 12.5304C5.82902 12.3897 5.75 12.1989 5.75 12C5.75 11.8011 5.82902 11.6104 5.96967 11.4697C6.11032 11.329 6.30109 11.25 6.5 11.25H16.19L13.47 8.53003Z"
                fill="white"
              />
            </svg>
          </button>
        </div>

                <div className="rounded-[14px] p-6 w-full min-h-[220px] relative overflow-hidden flex flex-col justify-end gap-3 bg-[#F6E7B8]">
          <img
            src="/images/ripndip/ripndip-landing.png"
            alt="ripndip-logo"
            className="absolute inset-0 h-full w-full object-cover object-top opacity-80"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />

          <div className="relative z-10 mx-auto rounded-full bg-white/85 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-black">
            223 x Ripndip x Ponke
          </div>

          <button
            onClick={() => navigate("/claim/ripndip")}
            className="bg-gradient-to-br from-[#DCCFA7] to-[#8B846D] text-white text-lg font-bold rounded-lg px-2 h-12 flex items-center justify-center gap-1.5 w-full relative z-10 shadow-lg hover:opacity-90 transition-opacity cursor-pointer"
          >
            <span>View RIPNDIP</span>

            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.47 8.53003C13.3375 8.38785 13.2654 8.19981 13.2688 8.00551C13.2723 7.81121 13.351 7.62582 13.4884 7.48841C13.6258 7.35099 13.8112 7.27228 14.0055 7.26885C14.1998 7.26543 14.3878 7.33755 14.53 7.47003L18.53 11.47C18.6705 11.6107 18.7493 11.8013 18.7493 12C18.7493 12.1988 18.6705 12.3894 18.53 12.53L14.53 16.53C14.4613 16.6037 14.3785 16.6628 14.2865 16.7038C14.1945 16.7448 14.0952 16.7668 13.9945 16.7686C13.8938 16.7704 13.7938 16.7519 13.7004 16.7142C13.607 16.6764 13.5222 16.6203 13.451 16.5491C13.3797 16.4778 13.3236 16.393 13.2859 16.2996C13.2482 16.2062 13.2296 16.1062 13.2314 16.0055C13.2332 15.9048 13.2552 15.8055 13.2962 15.7135C13.3372 15.6215 13.3963 15.5387 13.47 15.47L16.19 12.75H6.5C6.30109 12.75 6.11032 12.671 5.96967 12.5304C5.82902 12.3897 5.75 12.1989 5.75 12C5.75 11.8011 5.82902 11.6104 5.96967 11.4697C6.11032 11.329 6.30109 11.25 6.5 11.25H16.19L13.47 8.53003Z"
                fill="white"
              />
            </svg>
          </button>
        </div>
      </div>

      {currentDrawer === "wallet_connect" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <div className="mb-4">
              <img src="/svg/223-logo.svg" alt="223 Logo" />
            </div>

            <div className="text-center space-y-2.5">
              <div className="text-2xl font-bold text-black">
                Connect Your Wallet
              </div>

              <div className="text-[#626262]">
                To claim your 223 Digital Collectible, connect your wallet to
                continue.
              </div>

              <div className="!mt-6">
                <ThirdwebWalletConnect showDropdown={false} />
              </div>
            </div>
          </div>
        </div>
      )}

      {currentDrawer === "open_in_metamask" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <div className="text-center space-y-2.5">
              <div className="text-2xl font-bold text-black">
                Open in MetaMask Browser
              </div>

              <div className="text-[#626262]">
                To continue with MetaMask, please open this page in the MetaMask
                browser.
              </div>

              <button
                onClick={() => openInMetamask()}
                className="bg-[#343434] h-10 text-white font-bold rounded-[50px] px-10 mt-4"
              >
                Open in MetaMask Browser
              </button>
            </div>
          </div>
        </div>
      )}

      {currentDrawer === "scan_chip" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <img src="/svg/scan-chip.svg" alt="scan-chip" />

            <div className="w-full">
              <div className="text-2xl font-bold text-center text-black">
                Please scan the NFC chip
              </div>

              <button
                onClick={() => setCurrentDrawer(null)}
                className="py-2 w-full font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800 !mt-6"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
